import { Resolver, Query, FieldResolver, Root, Args, Arg, Ctx, Directive } from 'type-graphql'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'

import { KpiOrgScope } from './kpi-org-scope'
import { KpiOrgScopeList } from './kpi-org-scope-type'

@Resolver(KpiOrgScope)
export class KpiOrgScopeQuery {
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiOrgScope!, {
    nullable: true,
    description: 'Fetch a single KPI org-scope mapping by its unique identifier.'
  })
  async kpiOrgScope(
    @Arg('id', { description: 'Unique identifier of the org-scope mapping to fetch.' }) id: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiOrgScope> {
    const { domain } = context.state

    return await getRepository(KpiOrgScope).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiOrgScopeList, {
    description: 'Fetch multiple KPI org-scope mappings with filters and pagination'
  })
  async kpiOrgScopes(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<KpiOrgScopeList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      domain,
      params,
      repository: await getRepository(KpiOrgScope),
      searchables: ['org', 'scope01', 'scope02', 'scope03', 'scope04', 'scope05']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [String], { description: 'Get distinct values for a specific scope field' })
  async kpiOrgScopeValues(
    @Arg('scopeField', { description: 'Scope field to get distinct values from' })
    scopeField: 'scope01' | 'scope02' | 'scope03' | 'scope04' | 'scope05',
    @Ctx() context: ResolverContext,
    @Arg('searchTerm', { nullable: true, description: 'Optional search term to filter values' }) searchTerm?: string
  ): Promise<string[]> {
    const { domain } = context.state

    const queryBuilder = getRepository(KpiOrgScope)
      .createQueryBuilder('orgScope')
      .select(`DISTINCT orgScope.${scopeField}`, 'value')
      .where('orgScope.domain_id = :domainId', { domainId: domain.id })
      .andWhere(`orgScope.${scopeField} IS NOT NULL`)
      .andWhere(`orgScope.${scopeField} != ''`)
      .andWhere('orgScope.active = true')

    if (searchTerm) {
      queryBuilder.andWhere(`orgScope.${scopeField} ILIKE :searchTerm`, {
        searchTerm: `%${searchTerm}%`
      })
    }

    const results = await queryBuilder.getRawMany()
    return results.map(r => r.value).filter(v => v)
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => [String], { description: 'Get distinct org values' })
  async kpiOrgValues(
    @Ctx() context: ResolverContext,
    @Arg('searchTerm', { nullable: true, description: 'Optional search term to filter org values' }) searchTerm?: string
  ): Promise<string[]> {
    const { domain } = context.state

    const queryBuilder = getRepository(KpiOrgScope)
      .createQueryBuilder('orgScope')
      .select('DISTINCT orgScope.org', 'value')
      .where('orgScope.domain_id = :domainId', { domainId: domain.id })
      .andWhere('orgScope.active = true')

    if (searchTerm) {
      queryBuilder.andWhere('orgScope.org ILIKE :searchTerm', {
        searchTerm: `%${searchTerm}%`
      })
    }

    const results = await queryBuilder.getRawMany()
    return results.map(r => r.value)
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiOrgScope, { nullable: true, description: 'Find org-scope mapping by org identifier' })
  async kpiOrgScopeByOrg(
    @Arg('org', { description: 'Organization identifier to search for' }) org: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiOrgScope | undefined> {
    const { domain } = context.state

    return await getRepository(KpiOrgScope).findOne({
      where: { domain: { id: domain.id }, org }
    })
  }

  @FieldResolver(type => Domain)
  async domain(@Root() kpiOrgScope: KpiOrgScope): Promise<Domain> {
    return kpiOrgScope.domainId && (await getRepository(Domain).findOneBy({ id: kpiOrgScope.domainId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() kpiOrgScope: KpiOrgScope): Promise<User> {
    return kpiOrgScope.creatorId && (await getRepository(User).findOneBy({ id: kpiOrgScope.creatorId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() kpiOrgScope: KpiOrgScope): Promise<User> {
    return kpiOrgScope.updaterId && (await getRepository(User).findOneBy({ id: kpiOrgScope.updaterId }))
  }
}
